import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'
import type { Document } from '@contentful/rich-text-types'
import { INLINES, BLOCKS } from '@contentful/rich-text-types'
import type { Options as RichTextRenderOptions } from '@contentful/rich-text-react-renderer'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import Typography from '@segment/matcha/components/Typography'
import Image from '@segment/matcha/components/Image'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import RichText from '@segment/matcha/recipes/RichText'

import internalSecurityPng2x from './assets/InternalSecurity@2x.png'
import internalSecurityWebp from './assets/InternalSecurity.webp'
import internalSecurityWebp2x from './assets/InternalSecurity@2x.webp'
import internalSecurityWebp3x from './assets/InternalSecurity@3x.webp'

const copy = {
  heading: `Internal Security`,
}

const feature1 = {
  heading: `Data encryption`,
  description: {
    content: [
      {
        content: [
          {
            data: {},
            marks: [],
            nodeType: `text`,
            value: `Your data is encrypted at rest and protected by TLS in transit. Your Segment password is hashed using bcrypt, and we manage our production `,
          },
          {
            content: [
              {
                data: {},
                marks: [],
                nodeType: `text`,
                value: `secrets`,
              },
            ],
            data: {
              uri: `https://segment.com/blog/the-right-way-to-manage-secrets/`,
            },
            nodeType: INLINES.HYPERLINK,
          },
          {
            data: {},
            marks: [],
            nodeType: `text`,
            value: ` with AWS tools.`,
          },
        ],
        data: {},
        nodeType: BLOCKS.PARAGRAPH,
      },
    ],
    data: {},
    nodeType: BLOCKS.DOCUMENT,
  } as Document,
}

const feature2 = {
  heading: `Rigorous product design and security testing`,
  description: `Our projects pass thorough security-design reviews, threat models, and regular pen tests using trusted security vendors. We also employ a public bug bounty for continuous assessment.`,
}

const feature3 = {
  heading: `Time-bound access`,
  description: [
    `We systematically limit internal access to critical tools and resources using time-based access.`,
    `For more details on security at Segment, download our Security Overview.`,
  ],
  link: {
    text: `Security at Segment`,
    url: `https://security.segment.com`,
  },
}

const options: RichTextRenderOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <StyledText tag="span" variant="pMedium">
        {children}
      </StyledText>
    ),
  },
}

const InternalSecurity: FC<PropsWithChildren<unknown>> = () => {
  return (
    <SectionBase variant="2Col" copyVariant="narrow" reverse={true}>
      <Section.Copy>
        <CopyTextBase>
          <CopyText.Heading variant="h2">{copy.heading}</CopyText.Heading>
        </CopyTextBase>
        <FeatureBoxList>
          <FeatureBoxBase alignment="left">
            <FeatureBox.Body>
              <FeatureBox.Title>{feature1.heading}</FeatureBox.Title>
              <FeatureBox.Text>
                <RichText data={feature1.description} options={options} />
              </FeatureBox.Text>
            </FeatureBox.Body>
          </FeatureBoxBase>
          <FeatureBoxBase alignment="left">
            <FeatureBox.Body>
              <FeatureBox.Title>{feature2.heading}</FeatureBox.Title>
              <FeatureBox.Text>{feature2.description}</FeatureBox.Text>
            </FeatureBox.Body>
          </FeatureBoxBase>
          <FeatureBoxBase alignment="left">
            <FeatureBox.Body>
              <FeatureBox.Title>{feature3.heading}</FeatureBox.Title>
              {feature3.description.map((paragraph, index) => (
                <FeatureBox.Text key={index}>{paragraph}</FeatureBox.Text>
              ))}
              <CopyText.Link href={feature3.link.url} size="small" target="_blank">
                {feature3.link.text}
              </CopyText.Link>
            </FeatureBox.Body>
          </FeatureBoxBase>
        </FeatureBoxList>
      </Section.Copy>
      <SectionContents>
        <StyledPicture>
          <source
            type="image/webp"
            srcSet={`${internalSecurityWebp}, ${internalSecurityWebp2x} 2x, ${internalSecurityWebp3x} 3x`}
          />
          <Image width="521" height="482" src={internalSecurityPng2x} alt="Internal Security" loading="lazy" />
        </StyledPicture>
      </SectionContents>
    </SectionBase>
  )
}

const FeatureBoxList = styled.div`
  display: grid;
  gap: 24px;
  grid-auto-flow: row;
  margin-top: 56px;
  padding: 0;
`

const SectionContents = styled(Section.Contents)`
  justify-content: center;
  align-self: center;
  display: flex;
  overflow: hidden;
  picture > img {
    object-fit: contain;
  }
`

const StyledText = styled(Typography)`
  color: ${({ theme }) => theme.palette.brand.darkAcai[500]};
  margin: 0;
`

const StyledPicture = styled.picture``

export default InternalSecurity
